import { type FormEvent, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { AuthFrame, Button, Field, Icon, TextInput } from '../components/ui/index.js';
import { apiRequest } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useUiPrefs } from '../lib/ui-prefs.js';
import { useAdminLocale } from '../lib/admin-locale.js';

const backLinkClass =
  'block text-center text-sm font-medium text-blue-600 hover:text-blue-800 dark:hover:text-blue-200';

const PASSWORD_MIN = 12;
const PASSWORD_MAX = 128;
const BLOCKED_PASSWORDS = new Set([
  'password',
  'password123',
  'changeme',
  'change_me',
  'replace_with_random_secret',
  'password12345',
]);

type PasswordChecks = {
  length: boolean;
  letter: boolean;
  digit: boolean;
  trimmed: boolean;
  notBlocked: boolean;
  match: boolean;
};

function evaluatePassword(password: string, confirm: string, emailHint?: string): PasswordChecks {
  const lower = password.toLowerCase();
  return {
    length: password.length >= PASSWORD_MIN && password.length <= PASSWORD_MAX,
    letter: /[A-Za-z]/.test(password),
    digit: /[0-9]/.test(password),
    trimmed: password === password.trim() && password.length > 0,
    notBlocked:
      password.length > 0 &&
      !BLOCKED_PASSWORDS.has(lower) &&
      (!emailHint || lower !== emailHint.trim().toLowerCase()),
    match: password.length > 0 && password === confirm,
  };
}

function passwordIsStrong(checks: PasswordChecks): boolean {
  return Object.values(checks).every(Boolean);
}

function PasswordStrengthHints(props: { checks: PasswordChecks }) {
  const { text } = useAdminLocale();
  const rows: Array<{ ok: boolean; label: string }> = [
    {
      ok: props.checks.length,
      label: text(`Τουλάχιστον ${PASSWORD_MIN} χαρακτήρες`, `At least ${PASSWORD_MIN} characters`),
    },
    { ok: props.checks.letter, label: text('Τουλάχιστον ένα γράμμα', 'At least one letter') },
    { ok: props.checks.digit, label: text('Τουλάχιστον έναν αριθμό', 'At least one number') },
    {
      ok: props.checks.trimmed,
      label: text('Χωρίς κενά στην αρχή ή στο τέλος', 'No leading or trailing spaces'),
    },
    {
      ok: props.checks.notBlocked,
      label: text(
        'Όχι εύκολα μαντέψιμος / ίδιος με το email',
        'Not easily guessed or the same as the email',
      ),
    },
    { ok: props.checks.match, label: text('Οι δύο κωδικοί ταιριάζουν', 'Both passwords match') },
  ];
  const score = rows.filter((row) => row.ok).length;
  const tone =
    score <= 2
      ? 'bg-rose-500'
      : score <= 4
        ? 'bg-amber-500'
        : score < rows.length
          ? 'bg-sky-500'
          : 'bg-emerald-500';
  return (
    <div className="space-y-2">
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
        <div
          className={`h-full rounded-full transition-all ${tone}`}
          style={{ width: `${(score / rows.length) * 100}%` }}
        />
      </div>
      <ul className="space-y-1 text-xs">
        {rows.map((row) => (
          <li
            key={row.label}
            className={
              row.ok
                ? 'text-emerald-700 dark:text-emerald-300'
                : 'text-slate-500 dark:text-slate-400'
            }
          >
            {row.ok ? '✓' : '○'} {row.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ChangePasswordPage() {
  const { text } = useAdminLocale();
  const { changePassword } = useSession();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const checks = useMemo(
    () => evaluatePassword(newPassword, confirmPassword),
    [newPassword, confirmPassword],
  );

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!passwordIsStrong(checks)) {
      setMessage(
        text(
          'Ο νέος κωδικός δεν πληροί τους κανόνες ασφαλείας ή δεν ταιριάζει με την επιβεβαίωση.',
          'The new password does not meet the security requirements or does not match the confirmation.',
        ),
      );
      return;
    }
    setMessage(await changePassword(currentPassword, newPassword));
  }

  return (
    <AuthFrame
      title={text('Αλλαγή κωδικού', 'Change password')}
      subtitle={text(
        'Ο λογαριασμός πρέπει να αλλάξει κωδικό πριν συνεχίσει.',
        'You must change the account password before continuing.',
      )}
    >
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-5">
        <Field label={text('Τρέχων κωδικός', 'Current password')}>
          <TextInput
            type="password"
            required
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </Field>
        <Field
          label={text('Νέος κωδικός', 'New password')}
          hint={text(
            'Ισχυρός κωδικός με γράμμα και αριθμό.',
            'Use a strong password with a letter and a number.',
          )}
        >
          <TextInput
            type="password"
            required
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
        </Field>
        <Field label={text('Επιβεβαίωση νέου κωδικού', 'Confirm new password')}>
          <TextInput
            type="password"
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        </Field>
        <PasswordStrengthHints checks={checks} />
        {message ? <p className="text-sm text-rose-700 dark:text-rose-300">{message}</p> : null}
        <Button type="submit" className="w-full" disabled={!passwordIsStrong(checks)}>
          <Icon name="save" className="size-4" /> {text('Αποθήκευση', 'Save')}
        </Button>
      </form>
    </AuthFrame>
  );
}

export function ForgotPasswordPage() {
  const { text } = useAdminLocale();
  const { productName } = useUiPrefs();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await apiRequest(
      '/v1/auth/forgot-password',
      { method: 'POST', body: JSON.stringify({ email }) },
      false,
    );
    setSent(true);
  }

  return (
    <AuthFrame
      title={text('Επαναφορά κωδικού', 'Reset password')}
      subtitle={text(
        `Θα στείλουμε οδηγίες επαναφοράς για τον λογαριασμό ${productName}, μόνο αν υπάρχει.`,
        `We will send reset instructions for the ${productName} account, if it exists.`,
      )}
    >
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-5">
        {sent ? (
          <p className="rounded-lg bg-emerald-50 dark:bg-emerald-950/40 px-4 py-3 text-sm text-emerald-800">
            {text(
              'Αν ο λογαριασμός υπάρχει, στάλθηκε οδηγία επαναφοράς.',
              'If the account exists, reset instructions have been sent.',
            )}
          </p>
        ) : (
          <>
            <Field label="Email">
              <TextInput
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
            <Button type="submit" className="w-full">
              <Icon name="mail" className="size-4" /> {text('Αποστολή', 'Send')}
            </Button>
          </>
        )}
        <Link to="/login" className={backLinkClass}>
          {text('Επιστροφή στη σύνδεση', 'Back to sign in')}
        </Link>
      </form>
    </AuthFrame>
  );
}

export function TokenPasswordPage({ mode }: { mode: 'reset' | 'invite' }) {
  const { text } = useAdminLocale();
  const [params] = useSearchParams();
  const token = (params.get('token') ?? '').trim();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const path = mode === 'reset' ? '/v1/auth/reset-password' : '/v1/auth/accept-invite';
  const checks = useMemo(
    () => evaluatePassword(password, confirmPassword),
    [password, confirmPassword],
  );
  const canSubmit = Boolean(token) && passwordIsStrong(checks);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!token) {
      setError(
        text(
          'Λείπει ο σύνδεσμος πρόσκλησης. Ανοίξτε ξανά τον σύνδεσμο από το email.',
          'The invitation link is missing. Open the link from the email again.',
        ),
      );
      return;
    }
    if (!passwordIsStrong(checks)) {
      setError(
        text(
          'Ο κωδικός δεν πληροί τους κανόνες ασφαλείας ή δεν ταιριάζει με την επιβεβαίωση.',
          'The password does not meet the security requirements or does not match the confirmation.',
        ),
      );
      return;
    }
    const response = await apiRequest(
      path,
      { method: 'POST', body: JSON.stringify({ token, password }) },
      false,
    );
    if (response.ok || response.status === 204) {
      setDone(true);
      setError(null);
      return;
    }
    setError(
      text(
        'Το αίτημα δεν έγινε δεκτό. Ο σύνδεσμος μπορεί να έληξε ή ο κωδικός είναι αδύναμος.',
        'The request was rejected. The link may have expired or the password may be too weak.',
      ),
    );
  }

  return (
    <AuthFrame
      title={
        mode === 'reset'
          ? text('Νέος κωδικός', 'New password')
          : text('Αποδοχή πρόσκλησης', 'Accept invitation')
      }
      subtitle={text(
        'Ο σύνδεσμος από το email χρησιμοποιείται μία φορά. Ο κωδικός πρέπει να είναι ισχυρός.',
        'The email link can be used once. The password must be strong.',
      )}
    >
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-5">
        {done ? (
          <p className="rounded-lg bg-emerald-50 dark:bg-emerald-950/40 px-4 py-3 text-sm text-emerald-800">
            {text('Ολοκληρώθηκε. Μπορείτε να συνδεθείτε.', 'Done. You can now sign in.')}
          </p>
        ) : !token ? (
          <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            {text(
              'Λείπει ο σύνδεσμος. Ανοίξτε ξανά τον σύνδεσμο από το email πρόσκλησης ή επαναφοράς.',
              'The link is missing. Open the invitation or reset link from the email again.',
            )}
          </p>
        ) : (
          <>
            <Field label={text('Νέος κωδικός', 'New password')}>
              <TextInput
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
            <Field label={text('Επιβεβαίωση κωδικού', 'Confirm password')}>
              <TextInput
                type="password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </Field>
            <PasswordStrengthHints checks={checks} />
            {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
            <Button type="submit" className="w-full" disabled={!canSubmit}>
              <Icon name="arrow" className="size-4" /> {text('Συνέχεια', 'Continue')}
            </Button>
          </>
        )}
        <Link to="/login" className={backLinkClass}>
          {text('Σύνδεση', 'Sign in')}
        </Link>
      </form>
    </AuthFrame>
  );
}
